import { useState } from "react";
import { Upload, FileDown, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { api, errMsg } from "../lib/api";

const HEAD = ["Code", "Name", "Department", "Shift Start", "Shift End", "Weekly Off", "Ram Pass No.", "Ram Pass Expiry", "Iqama No.", "Iqama Expiry", "Airport PRN", "Status", "Status Date"];
const SAMPLE = ["1001", "Worker Name", "Site A", "07:00", "16:00", "Friday", "RP-0001", "2026-12-31", "2400000000", "2027-06-30", "PRN-0001", "Active", ""];

const downloadTemplate = () => {
  const csv = [HEAD, SAMPLE].map((l) => l.map((v) => `"${v}"`).join(",")).join("\r\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
  a.download = "workers_import_template.csv";
  a.click();
};

const Stat = ({ label, value, tone, testId }) => (
  <div className="rounded-lg border border-slate-200 p-3 text-center">
    <p className="text-xs text-slate-500">{label}</p>
    <p data-testid={testId} className={`font-mono text-2xl font-bold ${tone}`}>{value}</p>
  </div>
);

export const ImportDialog = ({ open, onOpenChange, onDone }) => {
  const [file, setFile] = useState(null);
  const [update, setUpdate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = (v) => { if (!v) { setFile(null); setResult(null); } onOpenChange(v); };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/workers/import", fd, { params: { update_existing: update } });
      setResult(data);
      toast.success(`Imported: ${data.created} added, ${data.updated} updated`);
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent data-testid="import-dialog" className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Import workers</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-slate-600">Upload an Excel (.xlsx) or CSV file. Required columns: <b>Code</b>, <b>Name</b>. Optional: Department, Shift Start, Shift End, Weekly Off, Ram Pass No., Ram Pass Expiry, Iqama No., Iqama Expiry, Airport PRN, Status (Active / Escaped / Terminated), Status Date. Dates as YYYY-MM-DD or DD/MM/YYYY.</p>
          <button type="button" onClick={downloadTemplate} data-testid="import-template-btn" className="text-sm text-emerald-700 hover:text-emerald-900 flex items-center gap-2"><FileDown size={14} /> Download template</button>
          <label className="block border-2 border-dashed border-slate-300 hover:border-slate-900 rounded-xl p-6 text-center cursor-pointer transition-colors">
            <Upload className="mx-auto text-slate-400" size={24} />
            <p data-testid="import-file-name" className="text-sm mt-2 text-slate-700">{file ? file.name : "Click to choose .xlsx or .csv"}</p>
            <input type="file" accept=".xlsx,.xlsm,.csv" className="hidden" data-testid="import-file-input" onChange={(e) => { setFile(e.target.files[0] || null); setResult(null); }} />
          </label>
          <label className="flex items-center gap-3 text-sm"><Switch checked={update} onCheckedChange={setUpdate} data-testid="import-update-existing-switch" /> Update workers whose code already exists</label>
          <button disabled={!file || busy} data-testid="import-submit-btn" className="w-full h-11 rounded-full bg-slate-900 hover:bg-emerald-500 hover:text-slate-950 text-white font-semibold disabled:opacity-50 transition-colors">
            {busy ? "Importing…" : "Import"}
          </button>
        </form>
        {result && <ImportResult r={result} />}
      </DialogContent>
    </Dialog>
  );
};

const ImportResult = ({ r }) => (
  <div className="space-y-3" data-testid="import-result">
    <p className="flex items-center gap-2 text-sm font-medium text-emerald-700"><CheckCircle2 size={16} /> {r.total} rows processed</p>
    <div className="grid grid-cols-4 gap-2">
      <Stat label="Added" value={r.created} tone="text-emerald-600" testId="import-created-count" />
      <Stat label="Updated" value={r.updated} tone="text-sky-600" testId="import-updated-count" />
      <Stat label="Skipped" value={r.skipped} tone="text-slate-600" testId="import-skipped-count" />
      <Stat label="Errors" value={r.errors.length} tone="text-rose-600" testId="import-error-count" />
    </div>
    {r.errors.length > 0 && (
      <div className="max-h-40 overflow-y-auto rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs space-y-1" data-testid="import-errors-list">
        {r.errors.map((e, i) => <p key={i} className="text-rose-700 flex gap-2"><AlertTriangle size={12} className="shrink-0 mt-0.5" /> Row {e.row}{e.code ? ` (${e.code})` : ""}: {e.error}</p>)}
      </div>
    )}
    {r.skipped > 0 && <p className="text-xs text-slate-500">Skipped rows have codes that already exist. Turn on "Update workers" to overwrite them.</p>}
  </div>
);
